import { useCallback, useEffect, useState } from 'react';
import { BarChart3, Calendar, Globe, Inbox, Loader2, MousePointerClick, TrendingUp, UserPlus } from 'lucide-react';
import client, { apiError } from './adminApi';

// Site analytics overview: anonymous page views, tracked clicks/events,
// referrers and the business funnel (enquiries, bookings, subscribers).
// Data is first-party and cookieless — counts only.
const EVENT_LABELS = {
  cta_book_consultation: 'Book consultation clicks',
  cta_contact: 'Contact clicks',
  contact_submit: 'Contact form submissions',
  newsletter_signup: 'Newsletter signups',
  timeline_filter: 'Timeline filter uses',
  challengefinder_apply: 'Solution finder uses',
  compass_page_viewed: 'Compass page views',
  compass_demo_click: 'Compass demo clicks',
  compass_pricing_click: 'Compass pricing clicks',
  compass_login_click: 'Compass login clicks',
  pulse_page_viewed: 'Pulse page views',
  pulse_demo_click: 'Pulse demo clicks',
  pulse_pricing_click: 'Pulse pricing clicks',
  foresee_page_viewed: 'Foresee page views',
  foresee_demo_click: 'Foresee demo clicks',
  foresee_pricing_click: 'Foresee pricing clicks',
  reach_page_viewed: 'Reach page views',
  reach_demo_click: 'Reach demo clicks',
  reach_pricing_click: 'Reach pricing clicks',
  reach_login_click: 'Reach login clicks',
  systems_showcase_tab_selected: 'Homepage systems tab switches',
  systems_showcase_explore_click: 'Homepage systems explores',
  case_study_view: 'Case study views',
  case_study_gallery_open: 'Gallery lightbox opens',
};

const label = (name) => EVENT_LABELS[name] || name.replace(/_/g, ' ');

export default function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async (d) => {
    try {
      setError('');
      const res = await client.get('/admin/analytics/summary', { params: { days: d } });
      setData(res.data);
    } catch (err) {
      setError(apiError(err, 'Could not load analytics.'));
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  const maxViews = Math.max(1, ...(data?.daily || []).map((d) => d.views));
  const delta = data && data.prevPageViews > 0
    ? Math.round(((data.pageViews - data.prevPageViews) / data.prevPageViews) * 100)
    : null;

  const stat = (icon, labelText, value, sub, testid) => (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500">
        {icon} {labelText}
      </div>
      <p className="mt-2 text-3xl font-extrabold tracking-tight text-navy-900">{value}</p>
      {sub && <p className="mt-1 text-xs font-semibold text-slate-500">{sub}</p>}
    </div>
  );

  const table = (title, rows, leftKey, rightKey, rightLabel, testid, leftRender) => (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h2 className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">{title}</h2>
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">No data in this period yet.</p>
      ) : (
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs font-bold uppercase text-slate-400">
              <th className="pb-2">{title.replace(/^Top /, '').replace(/s$/, '')}</th>
              <th className="pb-2 text-right">{rightLabel}</th>
            </tr>
          </thead>
          <tbody data-testid={testid}>
            {rows.map((row) => (
              <tr key={row[leftKey]} className="border-b border-slate-100 last:border-0">
                <td className="py-2 pr-3 font-semibold text-navy-900">{leftRender ? leftRender(row[leftKey]) : row[leftKey]}</td>
                <td className="py-2 text-right font-mono text-xs font-bold text-slate-600">{row[rightKey].toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div data-testid="admin-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-navy-900">Site Analytics</h1>
          <p className="mt-1 text-sm text-slate-500">Anonymous, cookieless first-party counting — no personal data.</p>
        </div>
        <div className="flex gap-1.5" role="group" aria-label="Period">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => { setData(null); setDays(d); }}
              data-testid={`analytics-period-${d}`}
              aria-pressed={days === d}
              className={`rounded-md px-3.5 py-2 text-xs font-bold transition-colors ${days === d ? 'bg-navy-900 text-white' : 'border border-slate-300 bg-white text-navy-900 hover:bg-navy-50'}`}
            >
              {d} days
            </button>
          ))}
        </div>
      </div>

      {error && <p role="alert" className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" data-testid="analytics-error">{error}</p>}
      {!data && !error && (
        <div className="flex justify-center py-20" role="status"><Loader2 className="h-6 w-6 animate-spin text-navy-900" /></div>
      )}

      {data && (
        <>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {stat(<MousePointerClick className="h-4 w-4" aria-hidden="true" />, 'Page views', data.pageViews.toLocaleString(),
              delta === null ? `${data.prevPageViews.toLocaleString()} previous period` : `${delta >= 0 ? '+' : ''}${delta}% vs previous ${data.days} days`,
              'analytics-stat-views')}
            {stat(<TrendingUp className="h-4 w-4" aria-hidden="true" />, 'Tracked actions', data.trackedEvents.toLocaleString(), 'Clicks & interactions', 'analytics-stat-events')}
            {stat(<Inbox className="h-4 w-4" aria-hidden="true" />, 'Enquiries', data.period.enquiries.toLocaleString(), `${data.totals.enquiries.toLocaleString()} all time`, 'analytics-stat-enquiries')}
            {stat(<Calendar className="h-4 w-4" aria-hidden="true" />, 'Bookings', data.period.bookings.toLocaleString(), `${data.totals.bookings.toLocaleString()} all time`, 'analytics-stat-bookings')}
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {stat(<UserPlus className="h-4 w-4" aria-hidden="true" />, 'New subscribers', data.period.subscribers.toLocaleString(), `${data.totals.subscribers.toLocaleString()} all time`, 'analytics-stat-subs')}
            {stat(<BarChart3 className="h-4 w-4" aria-hidden="true" />, 'Enquiry rate',
              data.pageViews > 0 ? `${((data.period.enquiries / data.pageViews) * 100).toFixed(1)}%` : '—',
              'Enquiries per page view', 'analytics-stat-conversion')}
          </div>

          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
            <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
              <Globe className="h-3.5 w-3.5" aria-hidden="true" /> Daily page views
            </h2>
            <div className="mt-4 flex h-36 items-end gap-[3px]" data-testid="analytics-chart" role="img" aria-label={`Daily page views for the last ${data.days} days`}>
              {data.daily.length === 0 && <p className="text-sm text-slate-500">No traffic recorded in this period yet.</p>}
              {data.daily.map((d) => (
                <div
                  key={d.date}
                  title={`${d.date}: ${d.views} views`}
                  className="flex-1 rounded-t bg-navy-900/80 transition-colors hover:bg-gold-600"
                  style={{ height: `${Math.max(3, (d.views / maxViews) * 100)}%` }}
                />
              ))}
            </div>
            {data.daily.length > 0 && (
              <div className="mt-2 flex justify-between text-[10px] font-bold uppercase tracking-widest text-slate-400">
                <span>{data.daily[0].date}</span>
                <span>{data.daily[data.daily.length - 1].date}</span>
              </div>
            )}
          </div>

          <div className="mt-6 grid gap-4 lg:grid-cols-3">
            {table('Top pages', data.topPages, 'path', 'views', 'Views', 'analytics-top-pages')}
            {table('Top actions', data.topEvents, 'event', 'count', 'Count', 'analytics-top-events', label)}
            {table('Top referrers', data.referrers, 'host', 'count', 'Visits', 'analytics-top-referrers')}
          </div>
        </>
      )}
    </div>
  );
}
